/**
 * Final-step redirect to the configured delivery URL.
 *
 * Honors config.delivery.openMode:
 *   - "top"     → break out of the iframe and navigate the top window
 *   - "new_tab" → open in a new tab with rel=noopener,noreferrer
 *   - else      → same-frame navigation
 *
 * Always passes the URL through isSafeUrl first (http/https only). Catches
 * cross-origin top access failures (publisher's site may forbid it) and
 * falls back to same-frame.
 *
 * `renderDeliveryUI` renders the "Redirecting..." card with the delivery
 * URL pill and a Restart button. It is PREVIEW-ONLY — it stands in for a
 * navigation the preview never performs. Production redirects straight to
 * the delivery URL with no interstitial (see render.ts).
 */

import { isSafeUrl } from '@/lib/safe-url';
import type { RuntimeDelivery } from '@/types/runtime';

const SVG_NS = 'http://www.w3.org/2000/svg';

export function redirectToDelivery(d: RuntimeDelivery): void {
  // TODO: if config.pgTrigger.enabled and placement ==
  // 'before_delivery', invoke the PG rewarded ad flow before redirecting.
  if (!isSafeUrl(d.url)) {
    console.warn('[quiz-runtime] unsafe or empty delivery URL, skipping redirect');
    return;
  }
  if (d.openMode === 'top') {
    try {
      (window.top ?? window).location.href = d.url;
      return;
    } catch {
      // Cross-origin frame access blocked — fall through to same-frame.
    }
  }
  if (d.openMode === 'new_tab') {
    window.open(d.url, '_blank', 'noopener,noreferrer');
    return;
  }
  window.location.href = d.url;
}

function makeIcon(width: number, pathsHTML: string): SVGElement {
  const svg = document.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('width', String(width));
  svg.setAttribute('height', String(width));
  svg.setAttribute('fill', 'none');
  svg.setAttribute('stroke', 'currentColor');
  svg.setAttribute('stroke-width', '2');
  svg.setAttribute('stroke-linecap', 'round');
  svg.setAttribute('stroke-linejoin', 'round');
  // Inline icon paths — generated SVG, no user input. textContent would
  // strip the markup entirely; innerHTML stays safe here.
  svg.innerHTML = pathsHTML;
  return svg;
}

const EXTERNAL_LINK_PATHS =
  '<path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>' +
  '<polyline points="15 3 21 3 21 9"/>' +
  '<line x1="10" y1="14" x2="21" y2="3"/>';

const REFRESH_PATHS =
  '<polyline points="1 4 1 10 7 10"/>' +
  '<path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/>';

export interface DeliveryUIOptions {
  url: string;
  /**
   * Preview only — the card exists to show the editor what the end of the
   * quiz does. Kept as a field (rather than dropped) so the call site reads
   * explicitly and a future non-preview surface has an obvious hook.
   */
  mode: 'preview';
  onRestart?: () => void;
  /**
   * Publisher's global CTA label (config.settings.ctaText). Rendered as an
   * inert button on the delivery card purely as a visual cue. It is NOT
   * wired to navigate — preview never navigates. Omitted/empty → no button.
   */
  ctaText?: string;
}

export function renderDeliveryUI(root: HTMLElement, opts: DeliveryUIOptions): void {
  root.replaceChildren();

  // Outer wrapper takes the full root and vertically centers the card.
  const wrap = document.createElement('div');
  wrap.style.flex = '1 1 auto';
  wrap.style.width = '100%';
  wrap.style.display = 'flex';
  wrap.style.flexDirection = 'column';
  wrap.style.alignItems = 'center';
  wrap.style.justifyContent = 'center';
  wrap.style.gap = '1rem';
  wrap.style.padding = '1rem';
  wrap.style.boxSizing = 'border-box';

  // Card
  const card = document.createElement('div');
  card.style.width = '100%';
  card.style.maxWidth = '22rem';
  card.style.border = '1px solid hsl(0, 0%, 90%)';
  card.style.borderRadius = '0.75rem';
  card.style.background = '#ffffff';
  card.style.padding = '1.5rem 1.25rem 1.25rem';
  card.style.display = 'flex';
  card.style.flexDirection = 'column';
  card.style.alignItems = 'center';
  card.style.gap = '0.5rem';
  card.style.textAlign = 'center';
  card.style.boxSizing = 'border-box';

  const icon = makeIcon(28, EXTERNAL_LINK_PATHS);
  icon.style.color = '#1a1a1a';
  card.appendChild(icon);

  const title = document.createElement('h2');
  title.textContent = 'Redirecting...';
  title.style.margin = '0';
  title.style.fontSize = '1.1rem';
  title.style.fontWeight = '600';
  title.style.color = '#1a1a1a';
  card.appendChild(title);

  const sub = document.createElement('p');
  sub.textContent = 'The user would be redirected to the URL configured in the selected option.';
  sub.style.margin = '0 0 0.5rem';
  sub.style.fontSize = '0.85rem';
  sub.style.color = 'hsl(0, 0%, 45%)';
  sub.style.lineHeight = '1.4';
  card.appendChild(sub);

  // URL pill: green status dot + truncated URL, full-width inside the card.
  const pill = document.createElement('div');
  pill.style.width = '100%';
  pill.style.padding = '0.5rem 0.85rem';
  pill.style.background = 'hsl(0, 0%, 96%)';
  pill.style.border = '1px solid hsl(0, 0%, 90%)';
  pill.style.borderRadius = '999px';
  pill.style.display = 'flex';
  pill.style.alignItems = 'center';
  pill.style.gap = '0.5rem';
  pill.style.fontSize = '0.85rem';
  pill.style.boxSizing = 'border-box';
  pill.style.minWidth = '0';

  const dot = document.createElement('span');
  dot.style.width = '8px';
  dot.style.height = '8px';
  dot.style.borderRadius = '50%';
  dot.style.background = '#10b981';
  dot.style.flexShrink = '0';
  pill.appendChild(dot);

  const urlText = document.createElement('span');
  urlText.textContent = opts.url || '(no URL configured)';
  urlText.style.overflow = 'hidden';
  urlText.style.textOverflow = 'ellipsis';
  urlText.style.whiteSpace = 'nowrap';
  urlText.style.color = '#1a1a1a';
  urlText.style.minWidth = '0';
  urlText.style.flex = '1 1 auto';
  pill.appendChild(urlText);

  card.appendChild(pill);

  // Global CTA label — visual cue only. The redirect flow is unchanged
  // (production auto-redirects on its timer; preview never navigates).
  // Publisher-authored string → textContent, never innerHTML.
  const ctaLabel = (opts.ctaText ?? '').trim();
  if (ctaLabel) {
    // Inert visual cue — no click handler, so it can't alter the redirect /
    // PG-reward flow. aria-hidden + tabindex -1 keep the non-interactive
    // element out of the tab order and the a11y tree.
    const cta = document.createElement('div');
    cta.setAttribute('data-quiz-cta', '');
    cta.setAttribute('aria-hidden', 'true');
    cta.textContent = ctaLabel;
    cta.style.width = '100%';
    cta.style.marginTop = '0.25rem';
    cta.style.padding = '0.65rem 1rem';
    cta.style.borderRadius = '0.5rem';
    cta.style.background = 'var(--quiz-primary)';
    cta.style.color = '#ffffff';
    cta.style.fontSize = '0.95rem';
    cta.style.fontWeight = '600';
    cta.style.textAlign = 'center';
    cta.style.boxSizing = 'border-box';
    card.appendChild(cta);
  }

  wrap.appendChild(card);

  if (opts.onRestart) {
    const restart = document.createElement('button');
    restart.type = 'button';
    restart.style.border = '1px solid hsl(0, 0%, 88%)';
    restart.style.background = '#ffffff';
    restart.style.borderRadius = '999px';
    restart.style.padding = '0.5rem 1rem';
    restart.style.display = 'inline-flex';
    restart.style.alignItems = 'center';
    restart.style.gap = '0.5rem';
    restart.style.fontSize = '0.9rem';
    restart.style.color = '#1a1a1a';
    restart.style.cursor = 'pointer';
    restart.style.fontFamily = 'inherit';

    const refreshIcon = makeIcon(16, REFRESH_PATHS);
    restart.appendChild(refreshIcon);

    const restartLabel = document.createElement('span');
    restartLabel.textContent = 'Restart quiz';
    restart.appendChild(restartLabel);

    const handler = opts.onRestart;
    restart.addEventListener('click', () => handler());
    wrap.appendChild(restart);
  }

  root.appendChild(wrap);
}
